import React, { useEffect, useRef, useState } from 'react';

import PageLayout from 'components/common/PageLayout';
import { LinkWithQuery } from 'components/common/LinkWithQuery';
import { SUBSCRIPTION_PLANS_PAGE } from 'components/constants';

import styles from './styles.module.scss';

export type PlatformDetailPageData = {
  eyebrow: string;
  title: string;
  subtitle: string;
  panelLabel: string;
  panelTitle: string;
  panelItems: string[];
  heroExperience?: {
    prompt: string;
    response: string;
    productEyebrow: string;
    productTitle: string;
    productText: string;
    context: string[];
  };
  heroDiagram?: {
    label: string;
    heading: string;
    input: string;
    context: string[];
    output: string;
    productTitle: string;
    productText: string;
    footer: string[];
  };
  stackedHero?: boolean;
  statementTitle: string;
  statementText: string;
  demo?: {
    eyebrow: string;
    title: string;
    text: string;
    prompt: string;
    response: string;
  };
  capabilitiesTitle: string;
  capabilitiesIntro: string;
  capabilities: Array<{ title: string; text: string; href?: string; linkLabel?: string }>;
  workflowTitle: string;
  workflowIntro: string;
  workflow: Array<{ title: string; text: string }>;
  outcomesTitle: string;
  outcomes: Array<{ title: string; text: string }>;
  compactOutcomes?: boolean;
  faqTitle?: string;
  faqIntro?: string;
  faqs?: Array<{ question: string; answer: string }>;
  relatedTitle?: string;
  relatedLinks?: Array<{ label: string; href: string; text: string }>;
  ctaTitle: string;
  ctaText: string;
  primaryCta?: { label: string; href: string; newTab?: boolean };
  secondaryCta?: { label: string; href: string; newTab?: boolean };
};

type Props = {
  data: PlatformDetailPageData;
  afterWorkflow?: React.ReactNode;
  appearance?: 'default' | 'immersive';
};

type RevealProps = {
  children: React.ReactNode;
  className?: string;
};

const ScrollReveal: React.FC<RevealProps> = ({ children, className = '' }) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.22, rootMargin: '0px 0px -8% 0px' },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={elementRef} className={`${styles.scrollReveal} ${isVisible ? styles.scrollRevealVisible : ''} ${className}`}>
      {children}
    </div>
  );
};

const PlatformDetailPage: React.FC<Props> = ({ data, afterWorkflow, appearance = 'default' }) => {
  const primaryCta = data.primaryCta || {
    label: 'Start creating',
    href: SUBSCRIPTION_PLANS_PAGE,
    newTab: true,
  };

  return (
    <PageLayout
      isFullWidth
      isWhiteHeader={appearance !== 'immersive'}
      isMarketingHeader={appearance !== 'immersive'}
      showFooterCta={false}
    >
    <main className={`${styles.page} ${appearance === 'immersive' ? styles.immersive : ''}`}>
      <section className={`${styles.hero} ${data.stackedHero ? styles.heroStacked : ''}`}>
        <div className={styles.heroInner}>
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>{data.eyebrow}</p>
            <h1>{data.title}</h1>
            <p className={styles.heroSubtitle}>{data.subtitle}</p>
            <a
              href={primaryCta.href}
              target={primaryCta.newTab ? '_blank' : undefined}
              rel="noopener noreferrer"
              className={styles.primaryCta}
            >
              {primaryCta.label}
              <span aria-hidden="true">→</span>
            </a>
          </div>

          {data.heroExperience ? (
            <div className={styles.heroExperience} aria-label="Storycards AI creation preview">
              <div className={styles.experienceToolbar}>
                <span>Storycards AI</span>
                <span className={styles.experienceStatus}><i />Live workspace</span>
              </div>
              <div className={styles.experienceBody}>
                <p className={styles.experiencePrompt}>{data.heroExperience.prompt}</p>
                <p className={styles.experienceResponse}>{data.heroExperience.response}</p>
                <div className={styles.experienceContext}>
                  {data.heroExperience.context.map((item) => <span key={item}>{item}</span>)}
                </div>
                <article className={styles.experienceCanvas}>
                  <div className={styles.experienceCanvasHeader}>
                    <span>Live canvas</span>
                    <em>Editable in Studio</em>
                  </div>
                  <div className={styles.experienceProduct}>
                    <small>{data.heroExperience.productEyebrow}</small>
                    <strong>{data.heroExperience.productTitle}</strong>
                    <p>{data.heroExperience.productText}</p>
                    <button type="button" tabIndex={-1}>Get started</button>
                  </div>
                </article>
              </div>
            </div>
          ) : data.heroDiagram ? (
            <div className={styles.heroDiagram} aria-label={data.heroDiagram.label}>
              <div className={styles.diagramHeader}>
                <div>
                  <span className={styles.diagramLabel}>{data.heroDiagram.label}</span>
                  <h2>{data.heroDiagram.heading}</h2>
                </div>
                <p>Start with a request and finish with an editable product that follows your organization’s rules.</p>
              </div>

              <div className={styles.organizationStory}>
                <ScrollReveal className={`${styles.storyStep} ${styles.requestStep}`}>
                  <div className={styles.storyRail} aria-hidden="true">
                    <span className={styles.storyNumber}>01</span>
                  </div>
                  <div className={styles.storyCopy}>
                    <small>Your request</small>
                    <h3>Ask naturally.</h3>
                    <p>Start with a prompt, URL or data brief. No rigid syntax and no technical setup.</p>
                  </div>
                  <div className={styles.storyVisual}>
                    <blockquote>{data.heroDiagram.input}</blockquote>
                  </div>
                </ScrollReveal>

                <ScrollReveal className={`${styles.storyStep} ${styles.contextStep}`}>
                  <div className={styles.storyRail} aria-hidden="true">
                    <span className={styles.storyNumber}>02</span>
                  </div>
                  <div className={styles.storyCopy}>
                    <small>Storycards intelligence</small>
                    <h3>Apply your organization’s context.</h3>
                    <p>Storycards uses the brand, language, components and permissions already defined by your team.</p>
                  </div>
                  <div className={styles.storyVisual}>
                    <p className={styles.contextLabel}>Organization context applied</p>
                    <ul className={styles.contextList}>
                      {data.heroDiagram.context.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </ScrollReveal>

                <ScrollReveal className={`${styles.storyStep} ${styles.outputStep}`}>
                  <div className={styles.storyRail} aria-hidden="true">
                    <span className={styles.storyNumber}>03</span>
                  </div>
                  <div className={styles.storyCopy}>
                    <small>{data.heroDiagram.output}</small>
                    <h3>Continue with full creative control.</h3>
                    <p>Every result opens as a real Storycards product, ready for your team to refine in the Studio.</p>
                    <div className={styles.storyProof}>
                      {data.heroDiagram.footer.map((item) => (
                        <span key={item}>{item}</span>
                      ))}
                    </div>
                  </div>
                  <div className={styles.storyVisual}>
                    <article className={styles.productPreview}>
                      <div className={styles.productPreviewBar}>
                        <span>Live canvas</span>
                        <em>Editable in Studio</em>
                      </div>
                      <div className={styles.productPreviewCanvas}>
                        <small>Mortgage advisor</small>
                        <strong>{data.heroDiagram.productTitle}</strong>
                        <p>{data.heroDiagram.productText}</p>
                        <button type="button" tabIndex={-1}>Get started</button>
                      </div>
                    </article>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          ) : (
            <div className={styles.heroPanel} aria-hidden="true">
              <span className={styles.panelLabel}>{data.panelLabel}</span>
              <h2>{data.panelTitle}</h2>
              <div className={styles.panelItems}>
                {data.panelItems.map((item, index) => (
                  <div key={item}>
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    <p>{item}</p>
                  </div>
                ))}
              </div>
              <div className={styles.panelStatus}>
                <i />
                Connected to your organization
              </div>
            </div>
          )}
        </div>
      </section>

      <section className={styles.statement}>
        <div className={styles.sectionInner}>
          <h2>{data.statementTitle}</h2>
          <p>{data.statementText}</p>
        </div>
      </section>

      {data.demo && (
        <section className={styles.demo}>
          <div className={styles.sectionInner}>
            <div className={styles.demoHeader}>
              <div>
                <p className={styles.eyebrow}>{data.demo.eyebrow}</p>
                <h2>{data.demo.title}</h2>
              </div>
              <p>{data.demo.text}</p>
            </div>

            <div className={styles.demoFrame} aria-label="Concept placeholder for the Storycards AI creation demo">
              <div className={styles.demoToolbar} aria-hidden="true">
                <span>Storycards AI workspace</span>
                <span>Live canvas</span>
              </div>
              <div className={styles.demoWorkspace} aria-hidden="true">
                <div className={styles.demoChat}>
                  <span>Shared conversation</span>
                  <p>{data.demo.prompt}</p>
                  <p>{data.demo.response}</p>
                  <div>Ask Storycards to refine the experience...</div>
                </div>
                <div className={styles.demoCanvas}>
                  <span>Editable in Studio</span>
                  <div className={styles.demoProduct}>
                    <small>Product advisor</small>
                    <strong>Find the right plan for you.</strong>
                    <p>Answer a few questions to get a personalized recommendation.</p>
                    <button type="button" tabIndex={-1}>Get started</button>
                  </div>
                </div>
              </div>
              <span className={styles.demoPlaceholder}>Video concept placeholder</span>
            </div>
          </div>
        </section>
      )}

      <section className={styles.capabilities}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2>{data.capabilitiesTitle}</h2>
            <p>{data.capabilitiesIntro}</p>
          </div>
          <div className={styles.capabilityGrid}>
            {data.capabilities.map((item, index) => {
              const content = (
                <>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                  {item.linkLabel && (
                    <strong>
                      {item.linkLabel}
                      <span aria-hidden="true">→</span>
                    </strong>
                  )}
                </>
              );

              return item.href ? (
                <LinkWithQuery className={styles.capabilityCard} to={item.href} key={item.title}>
                  {content}
                </LinkWithQuery>
              ) : (
                <article className={styles.capabilityCard} key={item.title}>
                  {content}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className={styles.workflow}>
        <div className={styles.sectionInner}>
          <div className={styles.workflowHeader}>
            <h2>{data.workflowTitle}</h2>
            <p>{data.workflowIntro}</p>
          </div>
          <div className={styles.workflowGrid}>
            {data.workflow.map((step, index) => (
              <article key={step.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {afterWorkflow}

      {data.outcomes.length > 0 && (
        <section className={styles.outcomes}>
          <div className={styles.sectionInner}>
            <h2>{data.outcomesTitle}</h2>
            <div className={`${styles.outcomeGrid} ${data.compactOutcomes ? styles.outcomeGridCompact : ''}`}>
              {data.outcomes.map((item) => (
                <article key={item.title}>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {data.faqs && data.faqs.length > 0 && (
        <section className={styles.faq} id="faq">
          <div className={styles.sectionInner}>
            <div className={styles.faqHeader}>
              <h2>{data.faqTitle || 'Questions teams ask before choosing a platform.'}</h2>
              {data.faqIntro && <p>{data.faqIntro}</p>}
            </div>
            <div className={styles.faqList}>
              {data.faqs.map((item, index) => (
                <details key={item.question} open={index === 0}>
                  <summary>
                    <span>{item.question}</span>
                    <i aria-hidden="true" />
                  </summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {data.relatedLinks && data.relatedLinks.length > 0 && (
        <nav className={styles.related} aria-label={data.relatedTitle || 'Related platform comparisons'}>
          <div className={styles.sectionInner}>
            <h2>{data.relatedTitle || 'Keep comparing.'}</h2>
            <div className={styles.relatedGrid}>
              {data.relatedLinks.map((item) => (
                <LinkWithQuery to={item.href} key={item.href}>
                  <span>{item.label}</span>
                  <p>{item.text}</p>
                  <i aria-hidden="true">→</i>
                </LinkWithQuery>
              ))}
            </div>
          </div>
        </nav>
      )}

      <section className={styles.cta}>
        <div className={styles.ctaInner}>
          <h2>{data.ctaTitle}</h2>
          <div>
            <p>{data.ctaText}</p>
            <div className={styles.ctaActions}>
              <a
                href={primaryCta.href}
                target={primaryCta.newTab ? '_blank' : undefined}
                rel="noopener noreferrer"
              >
                {primaryCta.label}
                <span aria-hidden="true">→</span>
              </a>
              {data.secondaryCta && (
                <a
                  href={data.secondaryCta.href}
                  target={data.secondaryCta.newTab ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  className={styles.secondaryCta}
                >
                  {data.secondaryCta.label}
                </a>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
    </PageLayout>
  );
};

export default PlatformDetailPage;
